import React from 'react';
import { Stethoscope, Flame, ShieldAlert, Phone, Heart, Shield } from 'lucide-react';
import { SOSService } from '../../data/sosNumbers';

/**
 * @component SOSButton
 * @description Botón individual para servicios de emergencia
 * CRÍTICO: Este componente puede salvar vidas - mantener accesible y confiable
 */

interface SOSButtonProps {
  /** Datos del servicio de emergencia */
  service: SOSService;
  /** País para analytics */
  country: string;
  /** Ubicación para analytics (sticky, sos_page) */
  location: string;
  /** Función para tracking de analytics */
  onSOSClick?: (service: string, country: string, location: string) => void;
  /** Tamaño del botón */
  size?: 'small' | 'medium' | 'large';
  /** Mostrar descripción */
  showDescription?: boolean;
}

const SOSButton: React.FC<SOSButtonProps> = ({
  service,
  country,
  location,
  onSOSClick,
  size = 'medium',
  showDescription = true
}) => {
  const getIcon = (iconName: string) => {
    const iconSize = size === 'small' ? 'h-4 w-4' : size === 'large' ? 'h-8 w-8' : 'h-6 w-6';
    
    switch (iconName) {
      case 'Stethoscope':
        return <Stethoscope className={iconSize} />;
      case 'Flame':
        return <Flame className={iconSize} />;
      case 'ShieldAlert':
        return <ShieldAlert className={iconSize} />;
      case 'Phone':
        return <Phone className={iconSize} />;
      case 'Heart':
        return <Heart className={iconSize} />;
      case 'Shield':
        return <Shield className={iconSize} />;
      default:
        return <ShieldAlert className={iconSize} />;
    }
  };

  const getButtonColor = (iconName: string) => {
    switch (iconName) {
      case 'Stethoscope':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500 border-red-600';
      case 'Flame':
        return 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500 border-orange-600';
      case 'ShieldAlert':
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 border-blue-600';
      case 'Phone':
        return 'bg-green-600 hover:bg-green-700 focus:ring-green-500 border-green-600';
      case 'Heart':
        return 'bg-pink-600 hover:bg-pink-700 focus:ring-pink-500 border-pink-600';
      case 'Shield':
        return 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 border-indigo-600';
      default:
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500 border-red-600';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'p-2 text-sm';
      case 'large':
        return 'p-6 text-lg';
      default:
        return 'p-4 text-base';
    }
  };

  const handleClick = () => {
    // Analytics tracking
    if (onSOSClick) {
      onSOSClick(service.numero, country, location);
    }

    // Abrir marcador telefónico
    window.location.href = service.tel;
  };

  return (
    <button
      onClick={handleClick}
      className={`
        ${getButtonColor(service.icono)}
        ${getSizeClasses()}
        text-white rounded-lg flex flex-col items-center justify-center
        transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
        active:scale-95 transform border-2 shadow-lg hover:shadow-xl
        w-full min-h-[80px]
      `}
      aria-label={`Llamar ${service.nombre} ${service.numero}`}
      type="button"
    >
      {getIcon(service.icono)}
      <span className="font-bold mt-2">{service.numero}</span>
      <span className="font-medium">{service.nombre}</span>
      {showDescription && (
        <span className="text-xs opacity-90 mt-1 text-center">
          {service.descripcion}
        </span>
      )}
    </button>
  );
};

export default SOSButton;
